import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Home, CalendarDays, Clock, Users, Settings as SettingsIcon, LogOut, User2, ListChecks, Bell, Mail, History as HistoryIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import ChangePasswordModal from "@/components/ChangePasswordModal";

const navStudent = [
  { to: "/", label: "Accueil", icon: Home, end: true },
  { to: "/dispos", label: "Dispos", icon: CalendarDays },
  { to: "/planning", label: "Planning", icon: ListChecks },
  { to: "/profil", label: "Profil", icon: User2 },
];

const navAdmin = [
  { to: "/", label: "Accueil", icon: Home, end: true },
  { to: "/dispos", label: "Dispos", icon: ListChecks },
  { to: "/planning", label: "Planning", icon: CalendarDays },
  // { to: "/heures-mensuel", label: "Heures", icon: Clock },  // Masqué — encodage effectué dans Odoo
  { to: "/equipe", label: "Équipe", icon: Users },
  { to: "/emails", label: "Emails", icon: Mail, adminOnly: true },
  { to: "/historique", label: "Historique", icon: HistoryIcon, adminOnly: true },
  { to: "/parametres", label: "Réglages", icon: SettingsIcon, adminOnly: true },
  { to: "/profil", label: "Profil", icon: User2 },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const items = (user?.role === "student" ? navStudent : navAdmin).filter(
    (i) => !i.adminOnly || user?.role === "admin"
  );

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top header */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-[#DDE7E4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <NavLink to="/" className="flex items-center gap-3 group" data-testid="logo-home-link">
            <img
              src="/logo-cantine.png"
              alt="La Cantine de Géraldine"
              className="h-11 w-auto object-contain"
              data-testid="app-logo"
            />
          </NavLink>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={`nav-${it.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `px-3.5 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${
                    isActive
                      ? "bg-[#2D5951] text-white shadow-sm"
                      : "text-[#1A2B27] hover:bg-[#F0F5F3]"
                  }`
                }
              >
                <it.icon className="w-4 h-4" />
                {it.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-sm font-semibold text-[#1A2B27]" data-testid="header-user-name">{user?.name}</span>
              <span className="text-[10px] uppercase tracking-wider text-[#94ADA7]" data-testid="header-user-role">{labelRole(user?.role)}</span>
            </div>
            {user && (user.role === "admin" || user.role === "chef") && <NotificationsBell />}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              data-testid="logout-button"
              className="rounded-full text-[#6b7f79] hover:text-[#2D5951]"
              aria-label="Se déconnecter"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 has-bottom-nav md:pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <Outlet />
          <ChangePasswordModal />
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-[#DDE7E4] shadow-[0_-8px_30px_rgb(62_39_35_/_0.06)]">
        <div className="grid grid-cols-5 h-[72px]" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
          {items.slice(0, 5).map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`bottomnav-${it.to.replace(/\//g, "") || "home"}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 text-[10px] font-semibold uppercase tracking-wide transition-colors ${
                  isActive ? "text-[#2D5951]" : "text-[#94ADA7]"
                }`
              }
            >
              <it.icon className="w-5 h-5" />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

function labelRole(r) {
  if (r === "admin") return "Admin";
  if (r === "chef") return "Chef d'atelier";
  return "Étudiant";
}

function NotificationsBell() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch (e) { console.warn("notifications load failed:", e); }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const markRead = async (nid) => {
    try {
      await api.post(`/notifications/${nid}/read`);
      await load();
    } catch (e) { console.warn("notification mark-read failed:", e); }
  };
  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      await load();
    } catch (e) { console.warn("notification mark-all failed:", e); }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          data-testid="notifications-bell"
          className="relative rounded-full text-[#6b7f79] hover:text-[#2D5951]"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <Badge
              data-testid="notifications-unread-count"
              className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-[#E53935] hover:bg-[#E53935] text-white text-[10px] font-bold flex items-center justify-center"
            >
              {unread > 9 ? "9+" : unread}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[360px] p-0 border-[#DDE7E4] shadow-lg"
        data-testid="notifications-panel"
      >
        <div className="flex items-center justify-between p-3 border-b border-[#DDE7E4]">
          <div className="font-display text-lg text-[#1A2B27]">Notifications</div>
          {unread > 0 && (
            <button
              onClick={markAll}
              data-testid="notifications-mark-all"
              className="text-xs text-[#2D5951] hover:underline font-semibold"
            >
              Tout marquer lu
            </button>
          )}
        </div>
        <ScrollArea className="max-h-[400px]">
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-[#94ADA7]">Aucune notification.</div>
          ) : (
            <div className="divide-y divide-[#DDE7E4]">
              {items.map((n) => {
                const isUnread = !(n.read_by || []).includes(user?.id);
                return (
                  <div
                    key={n.id}
                    onClick={() => markRead(n.id)}
                    data-testid={`notification-${n.id}`}
                    className={`p-3 cursor-pointer hover:bg-[#FFFFFF] ${isUnread ? "bg-[#F5FAF8]" : ""}`}
                  >
                    <div className="font-semibold text-sm text-[#1A2B27] flex items-start gap-2">
                      <span className="w-2 h-2 rounded-full bg-[#2D5951] mt-1.5 shrink-0" />
                      <span className="flex-1">{n.title}</span>
                    </div>
                    <pre className="whitespace-pre-wrap text-xs text-[#6b7f79] mt-1 ml-4 font-sans">
                      {n.message}
                    </pre>
                    <div className="text-[10px] text-[#B8C7C2] mt-1.5 ml-4">
                      {new Date(n.created_at).toLocaleString("fr-BE", { dateStyle: "short", timeStyle: "short" })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
